import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Select,
  SkeletonRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { MyBidsPage as MyBidsPageData } from '@bia-auction/shared-schema';
import { myBidsPageSchema } from '@bia-auction/shared-schema';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Gavel } from 'lucide-react';
import { useState } from 'react';
import { ErrorMsg } from '../components/Feedback';
import { PageHero } from '../components/PageHero';
import { Pagination } from '../components/Pagination';
import { api } from '../lib/api';
import { formatDateTime, formatKurus } from '../lib/format';
import { PHOTOS } from '../lib/photos';

/** 100 is the server's cap (`paginationQuerySchema`), so it is the longest page on offer. */
const PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * Green (`--tx-brand`, "Green text & links" in docs/15 §5) is the inline-link colour used by
 * every other link in both SPAs. The focus ring is explicit rather than left to the browser
 * default — docs/15 §1: "a visible focus state is an accessibility requirement".
 */
const LINK =
  'inline-flex items-center gap-1 rounded-sm text-tx-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * A bid row links back to its car only while there is still something to do there: a running
 * auction, or one that has not started (where an early bid is exactly what this row is).
 * A closed or cancelled sale is history, and a link that lands on a finished lot is noise.
 */
function isActionable(state: MyBidsPageData['items'][number]['saleState']): boolean {
  return state === 'LIVE' || state === 'PAUSED' || state === 'PUBLISHED';
}

export function MyBidsPage() {
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState<number>(50);
  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'bids', limit, offset],
    queryFn: () =>
      api<MyBidsPageData>(`/me/bids?limit=${limit}&offset=${offset}`, {}, myBidsPageSchema),
    // Paging changes the query key, so without this every page turn unmounts the list *and
    // the controls above it* back to a skeleton — the page-size select would disappear under
    // the hand that just used it. Keep the previous page on screen until the next arrives.
    placeholderData: keepPreviousData,
  });

  const empty = !data || data.total === 0;

  if (isLoading || error || empty)
    // The hero stays through loading, failure and emptiness alike — a titleless page reads as
    // one that failed to load rather than one with nothing to list.
    return (
      <div className="space-y-6">
        <PageHero photo={PHOTOS.yardWide} title={t('bidder.myBids.title')} />
        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : error ? (
          <ErrorMsg error={error} />
        ) : (
          <EmptyState
            icon={Gavel}
            title={t('bidder.myBids.empty')}
            action={
              <Button asChild variant="outline" size="sm">
                <Link to="/">{t('bidder.nav.auctions')}</Link>
              </Button>
            }
          />
        )}
      </div>
    );

  return (
    <div className="space-y-6">
      <PageHero
        photo={PHOTOS.yardWide}
        title={t('bidder.myBids.title')}
        subline={t('bidder.myBids.subline', { n: data.total })}
      />

      {/* Page size sits with the list it governs. Changing it resets the offset — keeping it
          would land the reader on an arbitrary row of a differently-sized page. */}
      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t('bidder.myBids.perPage')}
          <Select
            className="w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {/* <640px: the 5-column table folds into cards (approved OQ-3). */}
      <div className="space-y-3 sm:hidden">
        {data.items.map((b) => (
          <Card key={b.id}>
            <CardContent className="space-y-1 p-4 text-sm">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium">{b.saleTitle ?? b.saleCode}</span>
                <Badge variant="secondary">{t(`bidKind.${b.kind}`)}</Badge>
              </div>
              {/* tabular-nums on the wrapper so the plate lines up in both branches. */}
              <div className="text-muted-foreground tabular-nums">
                {isActionable(b.saleState) ? (
                  <Link
                    to="/auctions/$auctionId/lots/$lotId"
                    params={{ auctionId: b.saleId, lotId: b.lotId }}
                    className={LINK}
                  >
                    {b.plateSnapshot ?? t('bidder.myBids.viewVehicle')}
                    <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                  </Link>
                ) : (
                  (b.plateSnapshot ?? t('common.none'))
                )}
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="font-head text-h3 font-semibold tabular-nums">
                  {formatKurus(b.amount, b.currency)}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatDateTime(b.placedAt)}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('bidder.myBids.sale')}</TableHead>
              <TableHead>{t('bidder.myBids.plate')}</TableHead>
              <TableHead>{t('bidder.myBids.amount')}</TableHead>
              <TableHead>{t('bidder.myBids.kind')}</TableHead>
              <TableHead>{t('bidder.myBids.time')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.saleTitle ?? b.saleCode}</TableCell>
                <TableCell className="tabular-nums">
                  {/* Straight to the car, but only while the auction can still be acted on. */}
                  {isActionable(b.saleState) ? (
                    <Link
                      to="/auctions/$auctionId/lots/$lotId"
                      params={{ auctionId: b.saleId, lotId: b.lotId }}
                      className={LINK}
                    >
                      {b.plateSnapshot ?? t('bidder.myBids.viewVehicle')}
                      <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                    </Link>
                  ) : (
                    (b.plateSnapshot ?? t('common.none'))
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{formatKurus(b.amount, b.currency)}</TableCell>
                <TableCell>{t(`bidKind.${b.kind}`)}</TableCell>
                <TableCell className="tabular-nums">{formatDateTime(b.placedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination offset={offset} limit={limit} total={data.total} onOffsetChange={setOffset} />
    </div>
  );
}
